import React, { FC, useEffect, useState, useMemo, useContext } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { WallboxSelectProps } from '@components/WallboxSelect/index';
import { WALLBOX_VALUES, WALLBOX_TYPE, isBrandAC } from '../../constants/main';
import BasketSelectSection from '@components/v2/BasketSelectSection';
import Link from '@components/Link';
import { ITrimAttribute } from '../../interfaces/Car';
import DealService from '../../services/deal/deal.service';
import { getSessionIdCookie } from '@utils/Session.utils';
import { useDispatch } from 'react-redux';
import { CarDetailsDuck } from '../../redux/carDetails/carDetails.duck';
import Spinner from '@components/Spinner';
import { theme } from '../../styles/theme';

const KEY_NAME_IN_EXTRA_FIELDS = 'wallbox';

const WallboxSelectTemplate: FC<WallboxSelectProps> = ({ className, generatedIndex }) => {
    const { t } = useTranslations();

    const dispatch = useDispatch();

    const { carDetails, currentDeal } = useCarDetailsDuck();

    const [isOpen, setIsOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isSelected, setIsSelected] = useState(currentDeal?.extraFields?.wallbox);

    const detailData = carDetails?.trimAttributes?.find(
        (attr: ITrimAttribute) => attr.type === WALLBOX_TYPE && attr.attributeCode === 'description'
    );
    const priceData = carDetails?.trimAttributes?.find(
        (attr: ITrimAttribute) => attr.type === WALLBOX_TYPE && attr.attributeCode === 'price'
    );

    const handleSelect = async (value: string) => {
        try {
            setIsLoading(true);

            const response = await DealService.updateDeal(
                {
                    extra_field: { [KEY_NAME_IN_EXTRA_FIELDS]: value === WALLBOX_VALUES.YES },
                },
                getSessionIdCookie(),
                false
            );

            dispatch(CarDetailsDuck.setCurrentDeal(response.data?.deal));
            setIsSelected(value === WALLBOX_VALUES.YES);
            setIsLoading(false);
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className={isOpen ? `${className} isOpen` : `${className} isClosed`}>
            <div>
                <Collapsible
                    lazyRender={false}
                    open={isOpen}
                    trigger={
                        <AccessibleButton
                            tabIndex={0}
                            onClick={() => setIsOpen(!isOpen)}
                            className="titleWrapperButton"
                        >
                            <div className="titleWrapper">
                                <div className="titleContainer">
                                    <span className="sectionNumber">{generatedIndex}.</span>
                                    <span className="title">{t('basket.wallbox.title')}</span>
                                </div>
                                <Icon
                                    className={isOpen ? 'isOpen' : 'isClose'}
                                    name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                                />
                            </div>
                        </AccessibleButton>
                    }
                >
                    <p className="desc" dangerouslySetInnerHTML={{ __html: t('basket.wallbox.description') }} />
                    <BasketSelectSection
                        leftSectionTitle={t('basket.wallbox.noOption.title')}
                        leftSectionDescription={t('basket.wallbox.noOption.description')}
                        rightSectionTitle={t('basket.wallbox.yesOption.title')}
                        rightSectionDescription={t('basket.wallbox.yesOption.description')}
                        leftSelected={KEY_NAME_IN_EXTRA_FIELDS in currentDeal?.extraFields && !isSelected}
                        rightSelected={KEY_NAME_IN_EXTRA_FIELDS in currentDeal?.extraFields && isSelected}
                        onClick={handleSelect}
                        leftValue={WALLBOX_VALUES.NO}
                        rightValue={WALLBOX_VALUES.YES}
                        rightDetail={{
                            image: detailData?.imageURL,
                            title: detailData?.title,
                            description: detailData?.description,
                            price: `${priceData?.description} ${priceData?.title}`,
                            label: t('basket.wallbox.recommended.label'),
                        }}
                    />

                    {isLoading ? (
                        <Spinner size={25} border={3} color={theme.colors.black} />
                    ) : (
                        <Link
                            label={t('basket.wallbox.link.label')}
                            onClick={() => window.open(t('basket.wallbox.link.href'), '_blank')}
                            primary={!isBrandAC}
                            secondary={isBrandAC}
                            marginTop={15}
                            withArrowIcon
                            className="link"
                        />
                    )}
                </Collapsible>
            </div>
        </div>
    );
};

export default WallboxSelectTemplate;
